import { useCallback, useEffect, useRef, useState } from "react";

import { createAssessmentState } from "../utils/recording/sixMinuteWalkAssessment";
import {
  clearStoredSixMinuteWalkAssessment,
  readStoredSixMinuteWalkAssessment,
  writeStoredSixMinuteWalkAssessment,
} from "../utils/recording/sixMinuteWalkStorage";

export function useSixMinuteWalkAssessment(uid) {
  const skipNextPersistRef = useRef(false);
  const [assessment, setAssessment] = useState(
    () => readStoredSixMinuteWalkAssessment(uid) || createAssessmentState(),
  );

  useEffect(() => {
    setAssessment(
      readStoredSixMinuteWalkAssessment(uid) || createAssessmentState(),
    );
  }, [uid]);

  useEffect(() => {
    if (skipNextPersistRef.current) {
      skipNextPersistRef.current = false;
      return;
    }

    writeStoredSixMinuteWalkAssessment(uid, assessment);
  }, [assessment, uid]);

  const replaceAssessment = useCallback(
    (nextAssessment) => {
      setAssessment(nextAssessment);
      writeStoredSixMinuteWalkAssessment(uid, nextAssessment);
    },
    [uid],
  );

  const updateAssessment = useCallback(
    (updater) => {
      setAssessment((currentAssessment) => {
        const nextAssessment =
          typeof updater === "function" ? updater(currentAssessment) : updater;
        writeStoredSixMinuteWalkAssessment(uid, nextAssessment);
        return nextAssessment;
      });
    },
    [uid],
  );

  const resetAssessment = useCallback(() => {
    const nextAssessment = createAssessmentState();
    replaceAssessment(nextAssessment);
    return nextAssessment;
  }, [replaceAssessment]);

  const clearAssessment = useCallback(() => {
    const nextAssessment = createAssessmentState();
    skipNextPersistRef.current = true;
    clearStoredSixMinuteWalkAssessment(uid);
    setAssessment(nextAssessment);
    return nextAssessment;
  }, [uid]);

  return {
    assessment,
    clearAssessment,
    replaceAssessment,
    resetAssessment,
    updateAssessment,
    setAssessment,
  };
}
